import { useCallback, useEffect, useRef, useState } from 'react'
import * as logger from '../../../services/logger'
import { getTeamEnergyPage } from '../services/api'
import { ITeamEnergyPageResponse } from '@vacationtracker/shared/types/team-energy'

interface IUseTeamEnergyPageState {
  data: ITeamEnergyPageResponse | null
  loading: boolean
  error: Error | null
}

export interface IUseTeamEnergyPageResult extends IUseTeamEnergyPageState {
  reload: () => void
}

interface IFetchedKey {
  selectedId: string | null
  trendWeeks: number | undefined
  token: number
}

export function useTeamEnergyPage(departmentId: string | undefined, trendWeeks?: number): IUseTeamEnergyPageResult {
  const [state, setState] = useState<IUseTeamEnergyPageState>({ data: null, loading: true, error: null })
  const [reloadToken, setReloadToken] = useState(0)
  const fetchedRef = useRef<IFetchedKey>({ selectedId: null, trendWeeks: undefined, token: -1 })

  useEffect(() => {
    // When the page loads without `departmentId` in the URL, the parent mirrors
    // the server-chosen default into the URL after the first response. That URL
    // update flips `departmentId` from `undefined` to a value we just loaded -
    // skip in that case so we don't refetch the same page, flash the skeleton,
    // and force MembersTable to unmount/remount.
    const fetched = fetchedRef.current
    if (departmentId && fetched.selectedId === departmentId && fetched.trendWeeks === trendWeeks && fetched.token === reloadToken) {
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))

    getTeamEnergyPage({ departmentId, trendWeeks })
      .then((data) => {
        if (cancelled) {
          return
        }
        fetchedRef.current = {
          selectedId: data.selected?.department.id ?? null,
          trendWeeks,
          token: reloadToken,
        }
        setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (cancelled) {
          return
        }
        logger.error('useTeamEnergyPage failed', error)
        setState({ data: null, loading: false, error: error as Error })
      })

    return () => {
      cancelled = true
    }
  }, [departmentId, trendWeeks, reloadToken])

  const reload = useCallback(() => setReloadToken((prev) => prev + 1), [])

  return { ...state, reload }
}
